// Better Stack (Logtail) — клиентский мониторинг ошибок.
//
// Без сторонних npm-пакетов для самого транспорта: шлём POST на HTTP-API
// BetterStack. Это убирает любые проблемы с резолвом модулей
// (`Can't resolve @logtail/browser`) и не тянет в бандл ~20 КБ ради
// одного POST.
//
// Источник ошибок — `reportError()`. Перед отправкой стек **символизируется**
// через stacktrace-js: он fetch'ит `.map`-файл по `//# sourceMappingURL=`
// из бандла и подменяет минифицированные координаты на исходные
// (`MiniAppHomePage.tsx:240:5`). Без этого `Error.stack` приходит в виде
// `at o (vendor.abc.bundle.js:1:12345)` — бесполезно для разбора.
//
// Подключение:
//   В `.env` положить токен из BetterStack (Sources → ваш источник):
//     BETTERSTACK_SOURCE_TOKEN=xxxxxxxxxxxxxxxx
//   Опционально (по умолчанию используется https://in.logs.betterstack.com):
//     BETTERSTACK_INGESTING_HOST=https://in.logs.betterstack.com
//
// Чтобы символикация работала — nginx должен раздавать `.map`-файлы
// рядом с `.js`. Если `.map` 404 — fallback на raw-стек.
//
// `dotenv-webpack` уже включён в webpack.config.js — переменные
// инлайнятся в бандл во время сборки.
//
// Если токен не задан — интеграция тихо отключается, ошибок не бросает.
import StackTrace from "stacktrace-js";
// dotenv-webpack заменяет литералы `process.env.X` на значения из .env
// во время сборки. Никаких runtime-проверок `typeof process` тут не нужно —
// в браузере `process` не существует, и такая проверка ломала интеграцию
// (TOKEN оставался undefined, хотя в бандле уже лежала строка).
const TOKEN = process.env.BETTERSTACK_SOURCE_TOKEN;
// Endpoint берём из .env, но если в нём забыли указать `https://` — добавим
// сами. Без схемы fetch воспринимает значение как относительный путь
// (`https://<origin>/<host>`) и ловит 405.
function normalizeEndpoint(value) {
    const fallback = "https://in.logs.betterstack.com";
    const trimmed = (value || "").trim().replace(/\/+$/, "");
    if (!trimmed)
        return fallback;
    if (/^https?:\/\//i.test(trimmed))
        return trimmed;
    return `https://${trimmed}`;
}
const ENDPOINT = normalizeEndpoint(process.env.BETTERSTACK_INGESTING_HOST);
const ENABLED = Boolean(TOKEN);
/** True если интеграция инициализирована — для условных проверок. */
export function isLogtailEnabled() {
    return ENABLED;
}
function send(payload) {
    if (!ENABLED)
        return;
    try {
        const body = JSON.stringify(payload);
        // keepalive: запрос не отменяется при unload — критично для onerror.
        const promise = fetch(ENDPOINT, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${TOKEN}`,
                "Content-Type": "application/json",
            },
            body,
            keepalive: true,
        });
        // Гасим сетевые ошибки самой отправки — мониторинг не должен ронять
        // приложение.
        void promise.catch(() => {
            /* swallow */
        });
    }
    catch {
        /* swallow */
    }
}
// Совпадает с AppErrorBoundary — один мягкий reload без цикла.
const RECOVERY_FLAG = "vp-recovered";
/** Человекочитаемое сообщение из произвольного rejection/error. */
function formatErrorMessage(error) {
    if (error instanceof Error) {
        return error.message || error.name || "Error";
    }
    if (typeof error === "string") {
        return error;
    }
    if (error == null) {
        return String(error);
    }
    if (typeof error === "object") {
        const rec = error;
        if (typeof rec.message === "string" && rec.message.trim()) {
            return rec.message;
        }
        if (typeof rec.error === "string" && rec.error.trim()) {
            return rec.error;
        }
        try {
            const json = JSON.stringify(error);
            // Пустой {} / [] — бесполезный unhandledrejection без стека.
            if (!json || json === "{}" || json === "[]") {
                return "";
            }
            return json;
        }
        catch {
            return Object.prototype.toString.call(error);
        }
    }
    return String(error);
}
/**
 * Шум: in-app bridge, opaque Script error., пустые unhandledrejection.
 */
function isThirdPartyBridgeNoise(error, context) {
    const message = formatErrorMessage(error);
    const stack = error instanceof Error
        ? `${error.stack ?? ""}`
        : "";
    const filename = String(context.filename ?? "");
    const haystack = `${message}\n${stack}\n${filename}`.toLowerCase();
    // Браузер намеренно скрывает детали cross-origin ошибок:
    // message="Script error.", filename="", lineno=0 — разбирать нечего.
    const isOpaqueScriptError = /^script error\.?$/i.test(message.trim()) &&
        (!filename || Number(context.lineno ?? 0) === 0);
    if (isOpaqueScriptError)
        return true;
    // unhandledrejection с reason={} / undefined / "" — стека нет, пользы нет.
    if (context.source === "unhandledrejection" &&
        (!message.trim() || message === "{}" || message === "[]")) {
        return true;
    }
    // Safari Private Mode / Block Cookies: storage кидает SecurityError.
    if (/operation is insecure/i.test(message) ||
        (error instanceof Error &&
            error.name === "SecurityError" &&
            /storage|localstorage|sessionstorage|cookie/i.test(haystack))) {
        return true;
    }
    return [
        "webkit.messagehandlers",
        "senddatatonative",
        "sendpagehidemessage",
        "enabledidusertypeonkeyboardlogging",
        "java object is gone",
        "javascriptbridge",
        "java bridge method",
        "iabjs://",
        // Firefox / Brave Reader Mode инжектит скрипт в документ страницы
        "__firefox__.reader",
        "window.__firefox__",
        "can't find variable: __firefox__",
        "cannot find variable: __firefox__",
        "__firefox__ is not defined",
    ].some((p) => haystack.includes(p));
}
/**
 * Nginx SPA-fallback отдал index.html вместо *.bundle.js → браузер парсит
 * `<!doctype…` как JS → SyntaxError: Unexpected token '<'.
 * React ErrorBoundary это не ловит (ошибка загрузки скрипта).
 */
function isHtmlServedAsJsChunk(error, context) {
    const message = error instanceof Error
        ? error.message
        : typeof error === "string"
            ? error
            : String(error ?? "");
    const filename = String(context.filename ?? "");
    const unexpectedLt = /unexpected token\s*['"]?</i.test(message) ||
        /unexpected token\s*</i.test(message);
    const looksLikeBundle = /\.bundle\.js(\?|$)/i.test(filename);
    return unexpectedLt && looksLikeBundle;
}
/** Один мягкий reload (без clear storage) — подтянуть свежий HTML/манифест. */
function softReloadOnce() {
    if (typeof window === "undefined")
        return false;
    let url;
    try {
        url = new URL(window.location.href);
    }
    catch {
        return false;
    }
    if (url.searchParams.has(RECOVERY_FLAG))
        return false;
    url.searchParams.set(RECOVERY_FLAG, "1");
    window.location.replace(url.toString());
    return true;
}
/**
 * Прогнать `Error` через stacktrace-js: библиотека fetch'ит `.map`-файл
 * по `//# sourceMappingURL=` и возвращает фреймы уже в исходных
 * координатах. Если что-то пошло не так (нет `.map`, CORS, парс упал) —
 * возвращаем undefined, и снаружи будем слать raw-стек.
 */
async function symbolicateStack(err) {
    try {
        const frames = await StackTrace.fromError(err, { offline: false });
        if (!frames.length)
            return undefined;
        return frames.map((f) => `    at ${f.toString()}`).join("\n");
    }
    catch {
        return undefined;
    }
}
/**
 * Отправить ошибку в BetterStack. Любые сбои самой отправки гасятся.
 *
 * Внутри асинхронно символизируется стек через source map. Снаружи
 * API остался синхронным (`void`), потому что вызывающим коду
 * результат отправки не нужен — это «выстрелил и забыл».
 */
export function reportError(error, context = {}) {
    if (!ENABLED)
        return;
    if (isThirdPartyBridgeNoise(error, context))
        return;
    const err = error instanceof Error
        ? error
        : new Error(formatErrorMessage(error) || "Unknown error");
    // Стек символизируется асинхронно — stacktrace-js делает fetch к
    // .map-файлу. Это занимает обычно <100мс, но в крайнем случае при
    // быстром unload страница может закрыться раньше, чем мы отправим
    // POST. Это редкий случай — большинство ошибок происходит при
    // активном использовании страницы.
    void (async () => {
        const pretty = await symbolicateStack(err);
        send({
            dt: new Date().toISOString(),
            level: "error",
            message: err.message,
            name: err.name,
            // Если символизация удалась — шлём читаемый стек. Raw оставляем
            // отдельным полем для дебага, если в pretty что-то странное.
            stack: pretty ?? err.stack,
            ...(pretty ? { stackRaw: err.stack } : {}),
            url: typeof window !== "undefined" ? window.location.href : undefined,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
            ...context,
        });
    })();
}
/**
 * Подвешивает глобальные обработчики `error` и `unhandledrejection`.
 * Вызывать один раз — рядом со стартом приложения.
 */
export function installGlobalErrorHandlers() {
    if (typeof window === "undefined")
        return;
    window.addEventListener("error", (event) => {
        const payload = event.error ?? event.message;
        const context = {
            source: "window.onerror",
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        };
        // HTML вместо JS-чанка — ErrorBoundary не срабатывает, делаем soft reload.
        if (isHtmlServedAsJsChunk(payload, context)) {
            reportError(payload, { ...context, chunkLoadHtmlFallback: true });
            softReloadOnce();
            return;
        }
        reportError(payload, context);
    });
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        const message = formatErrorMessage(reason);
        // Webpack иногда режет чанк через rejection, не через ErrorBoundary.
        if (/loading chunk \d+ failed/i.test(message) ||
            /failed to fetch dynamically imported module/i.test(message)) {
            reportError(reason, { source: "unhandledrejection", chunkLoad: true });
            softReloadOnce();
            return;
        }
        reportError(reason, { source: "unhandledrejection" });
    });
}
